import { EmailBuilderVariantImage } from "@redotech/redo-merchant-marketing-rpc/schema/product-images/get-product-images";
import { DiscountSettings } from "@redotech/redo-model/discount/discount-db-parser";
import { ScreenSize } from "@redotech/redo-model/email-builder";
import { CropConfigV2 } from "@redotech/redo-model/email-template";
import { FulfillmentOrder } from "@redotech/redo-model/fulfillment";
import { TrackingPageDiscountSource } from "@redotech/redo-model/generated-discount-code-definition";
import { Provider } from "@redotech/redo-model/order";
import { OrderTrackingOrderSource } from "@redotech/redo-model/order-tracking/order-tracking-types";
import { TrackingPageSettings } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type { TrackingPageOrder } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { ShopifyGraphQLProduct } from "@redotech/redo-model/product";
import { RecommendedProductFilter } from "@redotech/redo-model/product-filters/product-filters-types";
import { RedoRecommendedProduct } from "@redotech/redo-model/redo-recommended-product-schema";
import { TrackableType } from "@redotech/redo-model/trackable";
import { createContext, ReactNode } from "react";

export enum TrackingPageEnvironment {
  BUILDER = "builder",
  CUSTOMER_PORTAL = "customer_portal",
}

// Simplified interface to only include the properties we actually need
export interface TrackingPageReturnAppSettings {
  storeUrl: string;
  merchantName: string;
  useShopifyStorefrontForCommentSold: boolean;
  exchanges: { shopSiteDomain: string; shopSiteURL: string };
}

// Collection interface for getAllCollections/getCollection
export interface CollectionInfo {
  id: string;
  name: string;
}

// Shared product upsell methods (used in both environments)
interface SharedProductUpsellMethods {
  getRecommendedBestSellingProducts: (
    numRecommendations: number,
  ) => Promise<RedoRecommendedProduct[]>;
  getRecommendedProductsByProvider: (params: {
    provider: OrderTrackingOrderSource;
    numRecommendations: number;
    excludedVariantIds: string[];
    excludedParentProductIds: string[];
    customer: { email?: string | null };
    recommendedProductFilterId?: string;
    productIds?: string[];
  }) => Promise<{ products: RedoRecommendedProduct[] }>;
  getAllCollections: () => Promise<CollectionInfo[]>;
  getCollection: (id: string) => Promise<CollectionInfo | null>;
  selectWindow: React.MutableRefObject<Window>;
  getProductById?: (params: {
    productId: string;
  }) => Promise<ShopifyGraphQLProduct | undefined | null>;
}

// Builder-only product upsell methods
interface BuilderProductUpsellMethods extends SharedProductUpsellMethods {
  getProductFilterById: (
    id: string,
  ) => Promise<RecommendedProductFilter | undefined>;
}

// Customer portal-only product upsell methods
interface CustomerPortalProductUpsellMethods
  extends SharedProductUpsellMethods {
  logPageClick: (params: {
    trackableId: string;
    trackableType: TrackableType;
    eventType: string;
    url: string;
    image?: string;
  }) => Promise<void>;
  redirectToWebsiteWithDiscountCode: (params: {
    discountCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmContent?: string;
    utmTerm?: string;
    settings: TrackingPageReturnAppSettings;
    window: React.MutableRefObject<Window>;
    selectedProduct?: {
      url: string;
      variantId?: string;
      selectedPlanId?: string;
    };
  }) => void;
}

// Subscription upsell methods (customer portal only)
// Shared subscription method interface
interface SharedSubscriptionUpsellMethods {
  getSubscriptionProducts?: (params: {
    search?: string;
    page?: number;
    pageSize?: number;
  }) => Promise<{
    products: {
      shopifyProductId?: number;
      title: string;
      description?: string | null;
      handle?: string;
      images?: {
        small?: string;
        medium?: string;
        large?: string;
        original?: string;
      }[];
      variants?: {
        externalVariantId: string;
        title: string;
        prices: { unitPrice: string };
        image?: {
          small?: string;
          medium?: string;
          large?: string;
          original?: string;
        };
      }[];
    }[];
    totalCount: number;
    hasNextPage: boolean;
  }>;
}

// Builder subscription methods (just for populating inspector)
interface BuilderSubscriptionUpsellMethods
  extends SharedSubscriptionUpsellMethods {}

// Customer portal subscription methods (includes all subscription functionality)
interface CustomerPortalSubscriptionUpsellMethods
  extends SharedSubscriptionUpsellMethods {
  createSubscription?: (params: {
    productId: string;
    customerEmail: string;
    variantId: string;
    quantity?: number;
    orderIntervalFrequency?: number;
    orderIntervalUnit?: "day" | "week" | "month";
    chargeIntervalFrequency?: number;
    customerFirstName?: string;
    customerLastName?: string;
    customerPhone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    province?: string;
    zip?: string;
    countryCode?: string;
    orderId?: string;
  }) => Promise<{
    subscriptionId?: string;
    status?:
      | "ACTIVE"
      | "CANCELLED"
      | "EXPIRED"
      | "active"
      | "cancelled"
      | "expired";
    nextChargeDate?: string;
    success: boolean;
  }>;
  checkCustomerSubscription?: (
    customerEmail: string,
    variantIds?: string[],
  ) => Promise<{
    customerExists: boolean;
    isSubscribedToProduct: boolean;
    hasActiveSubscriptions: boolean;
  }>;
  getSubscriptionPlans?: (params: {
    productId: string;
  }) => Promise<{
    plans: {
      id: number;
      externalPlanId: string;
      planName: string;
      sellingPlanId?: string | null;
      discountAmount?: string | null;
      discountType?: string | null;
      billingPolicy: { interval: string; intervalCount: number };
      deliveryPolicy: { interval: string; intervalCount: number };
    }[];
  }>;
}

// Image upload methods (shared by both builder and customer portal)
export interface ImageUploadMethods {
  getProductImages: (params: {
    searchString: string;
    pageSize: number;
    afterCursor?: string;
  }) => Promise<{
    images: EmailBuilderVariantImage[];
    nextPageCursor: string | null;
  }>;
  editImageWithAi: (params: {
    imageUrl: string;
    prompt: string;
  }) => Promise<{ imageUrl: string }>;
  cropImage: (params: {
    imageUrl: string;
    cropConfig: CropConfigV2;
  }) => Promise<{ imageUrl: string }>;
  getOriginalImageUrl: (params: {
    imageUrl: string;
  }) => Promise<{ originalImageUrl: string | null }>;
}

// Base context fields shared by both builder and customer portal
interface BaseTrackingPageContext {
  stalledDays: number | null;
  trackingPageId: string;
  pageSettings: TrackingPageSettings | undefined;
  screenSize?: ScreenSize;
  order: TrackingPageOrder | undefined;
  returnAppSettings: TrackingPageReturnAppSettings;
  fulfillmentOrders: FulfillmentOrder[];
  renderReturnSteps: () => ReactNode | null;
  subscribeUrl: string;
  hasSubscriptionIntegration?: boolean;
  generateDiscountCode: (
    discountId: string,
    source: TrackingPageDiscountSource,
    orderId?: string,
  ) => Promise<{ discountCode?: string; discountSettings?: DiscountSettings }>;
  imageMethods?: ImageUploadMethods;
}

// Builder context (merchant side)
export interface BuilderTrackingPageContext extends BaseTrackingPageContext {
  environment: TrackingPageEnvironment.BUILDER;
  /** @deprecated Prefer `availableProviders` / `defaultProvider`. */
  providers: Provider[];
  /** Providers the team has connected — feeds the provider selector. */
  availableProviders: Provider[];
  /** Team's default provider, if any. */
  defaultProvider?: Provider;
  productUpsell: BuilderProductUpsellMethods;
  subscriptionUpsell?: BuilderSubscriptionUpsellMethods;
  forceRehydrateBlock?: (blockId: string) => void;
  blockRehydrationCounters?: Record<string, number>;
}

// Customer portal context (storefront side)
export interface CustomerPortalTrackingPageContext
  extends BaseTrackingPageContext {
  environment: TrackingPageEnvironment.CUSTOMER_PORTAL;
  trackingUpdates?: {
    submit: (params: {
      phoneNumber: string;
      marketingConsent: boolean;
    }) => Promise<{ success: boolean; error?: string }>;
  };
  productUpsell: CustomerPortalProductUpsellMethods;
  subscriptionUpsell?: CustomerPortalSubscriptionUpsellMethods;
}

// Discriminated union for type safety
export type TrackingPageContext =
  | BuilderTrackingPageContext
  | CustomerPortalTrackingPageContext;

export const TrackingPageContext = createContext<
  TrackingPageContext | undefined
>(undefined);
